import type { ThemeColors } from "./theme.types"

export class DarkTheme implements ThemeColors {
  readonly name = "dark" as const

  readonly bgCanvas = "#000000"
  readonly bgSurface = "#1E1E1E"
  readonly textPrimary = "#F9FAFB"
  readonly textSecondary = "#9CA3AF"
  readonly borderDefault = "#374151"
  readonly accentPrimary = "#7B96D4"
  readonly accentPrimaryActive = "#5E94FF"
  readonly textOnPrimary = "#F9FAFB"
  readonly accentFav = "#F59E0B"
  readonly neutralLine = "#374151"
  readonly shadowSm = "rgba(255, 255, 255, 0.15)"
  readonly shadowLg = "rgba(255, 255, 255, 0.25)"
  readonly iconFilterMono = "brightness(0) invert(1)"

  readonly iconOverlayColor = "#E0E0E0"
  readonly iconOverlayOpacity = 0.4   // 30-50%

  readonly button = {
    primaryBg: "#5E94FF",
    primaryHover: "#60A5FA",
    primaryActive: "#5E94FF",
    primaryShadowHover: "rgba(94, 148, 255, 0.3)",
    textOnPrimary: "#F9FAFB",
    secondaryBg: "#1E1E1E",
    textOnSecondary: "#041226",
    disabledBg: "#262626",
    disabledText: "#525252",
    disabledBorder: "#333333",
    successBg: "#059669",
    successText: "#ECFDF5",
    successHover: "#10B981",
    successPressed: "#15AF84",
  }

  readonly folder = {
    filledBg: "#5B8CFF",
    textPrimary: "#DBDEE1",
    textSub: "#949BA4",
  }

  readonly destructive = {
    text: "#FF8877",
    textHighlight: "#FF8877",
    hover: "#2D1A1A",
    active: "#451A1A",
    card: "#1C1F26",
  }

  readonly presence = {
    active: "#4AF626",
    away: "#F59E0B",
  }

  readonly codeEditor = {
    bg: "#0D1117",
    gutterBg: "#0D1117",
    lineNumber: "#484F58",
    activeLine: "#161B22",
    selection: "#264F78",
  }

  readonly tooltip = {
    bgMain: "#1A1A1A",
    textLabel: "#F8FAFC",
    bgShortcut: "#333333",
    textShortcut: "#94A3B8",
    bgCode: "#0F1115",
    textGutter: "#475569",
    borderCode: "#2D323D",
    codeCmd: "#709CFF",
    codeBraces: "#F8FAFC",
    codeParam: "#E2E8F0",
    codeKeyword: "#709CFF",
    codeString: "#E6EDF3",
    codeParameter: "#FFA657",
    codeArgument: "#7EE787",
    codeComment: "#8B949E",
    codeMath: "#D2A8FF",
  }

  readonly input = {
    bg: "#0D1117",
    border: "#30363D",
    borderFocus: "#5E94FF",
    borderError: "#F87171",
    error: "#F87171",
    errorBg: "#2D1A1A",
    errorIcon: "#F87171",
    successText: "#A7F3D0",
    success: "#34D399",
    successBg: "#132D21",
    warning: "#FBBF24",
    warningText: "#2D2305",
    warningBg: "#FDE68A",
    textTyped: "#F8FAFC",
    textPlaceholder: "#475569",
    iconResting: "#94A3B8",
    iconActive: "#5E94FF",
  }

  readonly social = {
    bg: "#1C1F26",
    border: "#2D323D",
    hover: "#2D323D",
    dividerSubtle: "#374151",
    icon: "#FFFFFF",
  }

  readonly nav = {
    bgActive: "#1A2B4B",
    textActive: "#FFFFFF",
    textRest: "#94A3B8",
    hover: "#3F4759",
  }

  readonly userCursors = [
    "#4D94FF", "#66FF66", "#FFB347", "#FF66FF", "#FFE680",
    "#50C878", "#B380FF", "#FF9F80", "#80DFFF", "#7FFFD4",
    "#FF6666", "#40E0D0", "#C8A2C8", "#FFD24D", "#66CDAA",
    "#DDA0DD", "#FF7F7F", "#A0FFFF", "#FFD180", "#CC99FF",
  ]

  readonly editorPanels = {
    header: {
      border: "#374151",
      backBg: "#000000",
      backBorder: "#374151",
      backText: "#E5E7EB",
      backHover: "#374151",
      compileBg: "#1F2937",
      compileHover: "#2A3341",
      compileActive: "#323D4E",
      pathText: "#D1D5DB",
      titleText: "#F3F4F6",
    },
    icon: {
      filter: "invert(1)",
    },
    ribbon: {
      bg: "#2D2D30",
      border: "#505050",
      text: "#D1D5DB",
      divider: "#505050",
      tabHover: "#3F3F3F",
      tabActiveBg: "#373737",
      tabActiveText: "#F3F4F6",
      fieldBorder: "#505050",
      fieldBg: "#373737",
      fieldHover: "#3F3F3F",
      fieldText: "#E5E7EB",
      iconHover: "#374151",
      groupActiveBg: "#3F3F3F",
      popupBg: "#2D2D30",
      groupBg: "#373737",
      groupDivider: "#505050",
    },
    explorer: {
      canvasBg: "#000000",
      sidebarBorder: "#374151",
      panelBorder: "#374151",
      panelShadow: "none",
      iconHoverBg: "#3F3F3F",
      selectedBg: "rgba(30, 58, 138, 0.3)",
      layersIconBg: "#1e1e1e",
      layersIconBorder: "0.5px solid #374151",
      layoutCanvasBg: "#000000",
    },
    explorerFrame: {
      background: "#1e1e1e",
      border: "#374151",
      hoverBg: "#3F3F3F",
      selectedBg: "#243B5C",
      textPrimary: "#F9FAFB",
      textMuted: "#9CA3AF",
      accent: "#5B8CFF",
      shadow: "#000000",
      danger: "#EF4444",
      folderIcon: "#7B96D4",
      fileIcon: "#7B96D4",
      chevronIcon: "#7B96D4",
      treeLine: "#484F58",
    },
    dock: {
      btnBorder: "#374151",
    },
    codeEditorPanel: {
      breadcrumbBg: "#252526",
      breadcrumbBorder: "#3e3e42",
      breadcrumbText: "#D1D5DB",
      breadcrumbDivider: "#4b4b4b",
      breadcrumbMutedText: "#9CA3AF",
    },
    console: {
      innerBg: "#1e1e1e",
      tabActiveBorder: "#9CA3AF",
      tabActiveBorderWidth: "0.5px",
      rowText: "#D1D5DB",
      rowBorder: "rgba(55, 65, 81, 0.6)",
      tabActiveText: "#F3F4F6",
      tabText: "#9CA3AF",
      tabHoverText: "#D1D5DB",
      emptyText: "#6B7280",
      codeBg: "rgba(0,0,0,0.20)",
      tabActiveBg: "#334155",
      warnRowBg: "rgba(69, 26, 3, 0.3)",
    },
    status: {
      errorBg: "rgba(127, 29, 29, 0.4)",
      errorBorder: "#991B1B",
      errorText: "#F87171",
      errorBadgeBg: "rgba(127, 29, 29, 0.5)",
      errorBadgeText: "#FCA5A5",
      warnBg: "rgba(69, 26, 3, 0.5)",
      warnBorder: "rgba(133, 77, 14, 0.6)",
      warnText: "#FACC15",
      warnBadgeBg: "rgba(113, 63, 18, 0.5)",
      warnBadgeText: "#FDE047",
      successBg: "rgba(5, 46, 22, 0.5)",
      successBorder: "rgba(22, 101, 52, 0.6)",
      successText: "#4ADE80",
      successBadgeBg: "rgba(20, 83, 45, 0.5)",
      successBadgeText: "#86EFAC",
      neutralBadgeBg: "rgba(55, 65, 81, 0.6)",
      linkText: "#60A5FA",
      linkHover: "#93C5FD",
      errorTextStrong: "#F87171",
      errorDismissHover: "#FCA5A5",
    },
    preview: {
      canvasBg: "#1e1e1e",
      wrapperBg: "#111827",
      bgAlt: "#111827",
      tabBarBg: "#1E293B",
      tabBorder: "#334155",
      contentBg: "#1F2937",
      text: "#D1D5DB",
      mutedText: "#9CA3AF",
      hintIcon: "#64748B",
      errorText: "#F87171",
      pageAreaBg: "#0F172A",
      imgBg: "#030712",
      imgBorder: "#1F2937",
    },
    pdf: {
      errorBg: "rgba(127, 29, 29, 0.3)",
      errorBorder: "#7F1D1D",
      errorText: "#FCA5A5",
    },
    toggle: {
      onBg: "#454545",
      trackBg: "#353535",
      trackBorder: "#555555",
      knobBg: "#525252",
      knobBorderOn: "#6B9AFF",
      knobBorderOff: "#6A7A8A",
      labelOn: "#F3F4F6",
      labelOff: "#6B7280",
    },
    dropdown: {
      triggerBg: "#383838",
      triggerBorder: "#505050",
    },
    versionHistory: {
      headerBorder: "#374151",
      titleText: "#F5F5F5",
      closeText: "#D4D4D4",
      closeHoverBg: "#262626",
      subtext: "#D4D4D4",
      groupLabelBorder: "#404040",
      groupLabelText: "#A3A3A3",
      emptyText: "#A3A3A3",
      itemHoverBg: "rgba(38, 38, 38, 0.8)",
      itemSelectedBorder: "#525252",
      itemSelectedBg: "rgba(38, 38, 38, 0.6)",
      itemTimeText: "#737373",
      itemNameText: "#A3A3A3",
      itemDescText: "#E5E5E5",
      itemHashText: "#737373",
      iconAccent: "#60A5FA",
      footerBorder: "#404040",
      footerBg: "rgba(23, 23, 23, 0.8)",
      footerLabel: "#A3A3A3",
      codeBorder: "#525252",
      codeBg: "#0A0A0A",
      codeText: "#E5E5E5",
      offlineBg: "rgba(69, 26, 3, 0.4)",
      offlineBorder: "rgba(120, 53, 15, 0.5)",
      offlineText: "#FEF3C7",
      scrollbarThumb: "#4B5563",
      panelShadow: "-2px 0 12px rgba(0,0,0,0.35)",
      loadingBorder: "#404040",
    },
    cookie: {
      border: "#374151",
      titleText: "#F3F4F6",
      bodyText: "#9CA3AF",
      btnBorder: "#4B5563",
      btnHover: "#1F2937",
    },
    tabs: {
      activeBg: "#111827",
      activeText: "#F3F4F6",
      inactiveText: "#9CA3AF",
      inactiveHoverText: "#D1D5DB",
      inactiveHoverBg: "#334155",
      barBg: "#1E293B",
      barBorder: "#334155",
      detachedBadgeBg: "#334155",
    },
    browserTab: {
      text: "#F9FAFB",
      btnBorder: "#484F58",
      btnBorderWidth: "0.2px",
      btnBgPdf: "#000000",
      btnBgFile: "#000000",
    },
    divider: {
      bg: "#000000",
    },
  }

  readonly dashboardPanels = {
    topbar: {
      text: "#FFFFFF",
      notifText: "#F9FAFB",
      notifTextStrong: "#FFFFFF",
      notifBadgeBlue: "rgba(59, 130, 246, 0.15)",
      notifBadgeRed: "rgba(239, 68, 68, 0.15)",
      notifBadgeOrange: "rgba(249, 115, 22, 0.15)",
      notifBadgeGreen: "rgba(34, 197, 94, 0.15)",
    },
    surfaceHover: "#3F4759",
    surfaceHoverMuted: "#3F4759",
    accentStrong: "#5E94FF",
    accentHover: "#60A5FA",
    strongText: "#FFFFFF",
    filenameText: "#F9FAFB",
    mutedTextAlt: "#9CA3AF",
    textPrimaryAlt: "#F9FAFB",
    textSecondary: "#9ca3af",
    textMutedAlt: "#9CA3AF",
    cardSelectedBg: "rgba(94, 148, 255, 0.15)",
    chipBg: "#000000",
    chipText: "#FFFFFF",
    dialogText: "#F9FAFB",
    dialogTextFaint: "#4B5563",
    tagBg: "#374151",
    warnBg: "rgba(217, 119, 6, 0.12)",
    warnBorder: "rgba(217, 119, 6, 0.35)",
    dangerBg: "rgba(220, 38, 38, 0.12)",
    dangerBorder: "rgba(220, 38, 38, 0.35)",
    iconMutedFilter: "brightness(0) invert(1) opacity(0.65)",
    highlightBg: "#854D0E",
    highlightText: "#FEF3C7",
  }
}

export const darkTheme = new DarkTheme()
